import { generateRandomString } from '@mntn-dev/utilities'
import type { ZodSimplify } from '@mntn-dev/utility-types'
import { z } from 'zod'
import { BillingScheduleSchema } from './billing/billing-schedule.ts'
import { CurrencySchema } from './billing/currency.ts'
import { InvoiceTermsSchema } from './billing/invoice-terms.ts'
import { NonEmptyStringSchema } from './property.models.ts'
import {
  BillingProfileIdSchema,
  OrganizationIdSchema,
} from './unique-id.models.ts'

export const BillingServiceIdSchema = z.string().brand('billing')
export type BillingServiceId = ZodSimplify<typeof BillingServiceIdSchema>

export const BillingServiceId = (value: string = generateRandomString(16)) => {
  return BillingServiceIdSchema.parse(value)
}
export const BillingServiceMethodIdSchema = z
  .string()
  .brand('billing-service-method')

export type BillingServiceMethodId = ZodSimplify<
  typeof BillingServiceMethodIdSchema
>

export const BillingServiceMethodId = (
  value: string = generateRandomString(16)
) => {
  return BillingServiceMethodIdSchema.parse(value)
}

export const BillingAddressSchema = NonEmptyStringSchema(256)
export type BillingAddress = ZodSimplify<typeof BillingAddressSchema>

/**
 * BillingProfileDomainSelectModel
 */

export const BillingProfileDomainSelectModelSchema = z.object({
  billingProfileId: BillingProfileIdSchema,
  organizationId: OrganizationIdSchema,
  billingServiceId: BillingServiceIdSchema.optional(),
  billingSchedule: BillingScheduleSchema,
  billingAddress: BillingAddressSchema.optional(),
  currency: CurrencySchema,
  invoiceTerms: InvoiceTermsSchema,
  defaultProfile: z.boolean(),
})

export type BillingProfileDomainSelectModel = ZodSimplify<
  typeof BillingProfileDomainSelectModelSchema
>

/**
 * BillingProfileDomainInsertModel
 */

export const BillingProfileDomainInsertModelSchema =
  BillingProfileDomainSelectModelSchema.partial({
    billingProfileId: true,
  })

export type BillingProfileDomainInsertModel = ZodSimplify<
  typeof BillingProfileDomainInsertModelSchema
>

/**
 * BillingProfileDomainUpdateModel
 */

export const BillingProfileDomainUpdateModelSchema =
  BillingProfileDomainSelectModelSchema.pick({
    billingSchedule: true,
    billingServiceId: true,
    currency: true,
    invoiceTerms: true,
    defaultProfile: true,
  })
    .extend({
      billingAddress: BillingAddressSchema.nullable(),
    })
    .partial()

export type BillingProfileDomainUpdateModel = ZodSimplify<
  typeof BillingProfileDomainUpdateModelSchema
>

/**
 * BillingProfileDomainSelectModelFilters
 */
export const BillingProfileDomainSelectModelFiltersSchema = z
  .object({
    billingProfileId: BillingProfileIdSchema,
    organizationId: OrganizationIdSchema,
    billingServiceId: BillingServiceIdSchema,
    billingSchedule: BillingScheduleSchema,
    currency: CurrencySchema,
    invoiceTerms: InvoiceTermsSchema,
    defaultProfile: z.boolean(),
  })
  .partial()

export type BillingProfileDomainSelectModelFilters = ZodSimplify<
  typeof BillingProfileDomainSelectModelFiltersSchema
>
